import ErrorMessage from 'components/ErrorMessage'
import React, { useContext, useRef, useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import FormData from 'form-data'
import AuthContext from 'context/AuthContext'
import OButton from 'components/reusable/OButton'
import { AiFillCamera } from 'react-icons/ai'
import OImage from 'components/reusable/OImage'
import { useTranslation } from 'react-i18next'
import { preventMaxInput } from 'utils/validations'
import formValidation from '../../../utils/formValidation'
import OInputField from 'components/reusable/OInputField'
import AvatarEditor from 'react-avatar-editor'
import { Slider } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import { apiGet } from '../../../utils/apiFetch'
import apiPath from '../../../utils/pathObj'
import useToastContext from 'hooks/useToastContext'
import LocationMap from 'components/GoogleMap/LocationMap'
import AddressAutocomplete from 'components/GoogleMap/AddressAutocomplete'
import { geocodeAddress } from 'utils/googleMaps'

const PersonalDetails = () => {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty }
  } = useForm({ mode: 'onChange', shouldFocusError: true, defaultValues: {} })
  const notification = useToastContext()
  const [profilePicError] = useState('')
  const [open, setOpen] = useState(false)
  const [updateProfileLoading, setUpdateProfileLoading] = useState(false)
  const { updateProfile, user, updatePageName } = useContext(AuthContext)
  const profilePicRef = useRef()
  const profilePicRefLogo = useRef()
  const asId = (value) => {
    if (!value) return ''
    if (typeof value === 'object') return String(value._id || '')
    return String(value)
  }
  const [recordsTemp, setRecordTemp] = useState('')
  const [listTemp, setListTemp] = useState('')
  const [cat, setCat] = useState(() => asId(user?.countryID))
  const [subCat, setSubCat] = useState(() => asId(user?.stateID))
  const [isCatError, setIsCatError] = useState(false)
  const [isSubCatError, setIsSubCatError] = useState(false)
  const [stateList, setStateList] = useState([])
  const [mapPin, setMapPin] = useState(null)
  const pendingStateRef = useRef(null)
  const countriesRef = useRef([])
  const stateListRef = useRef([])
  const catRef = useRef('')
  catRef.current = cat
  countriesRef.current = Array.isArray(recordsTemp) ? recordsTemp : []
  stateListRef.current = stateList
  useEffect(() => {
    reset({
      name: user.name,
      officialEmail: user.officialEmail,
      profilePicture: user.profilePicture,
      logo: user.logo,
      countryID: user?.countryID,
      stateID:user?.stateID,
      city: user?.city,
      zipcode: user?.zipcode,
      address: user?.address,
      companyNumber: user?.companyNumber
    })
  }, [user])

  var editor = ''
  var editorLogo = ''
  const [picture, setPicture] = useState({
    cropperOpen: false,
    img: null,
    zoom: 2,
    croppedImg: user?.profilePicture
  })
  const [openLogo, setOpenLogo] = useState(false)
  const [logo, setLogo] = useState({
    cropperOpen: false,
    img: null,
    zoom: 2,
    croppedImg: user?.logo
  })

  const handleSlider = (value, pic, setPic) => {
    setPic({
      ...pic,
      zoom: value
    })
  }

  const handleCancel = (pic, setPic, setOp) => {
    setPic({
      ...pic,
      cropperOpen: false
    })
    setOp(false)
  }

  const setEditorRef = ed => {
    editor = ed
  }

  const setEditorRefLogo = ed => {
    editorLogo = ed
  }

  const handleSave = (pic, setPic, setOp, edit, setEditor) => {
    if (setEditor) {
      const canvasScaled = edit.getImageScaledToCanvas()
      const croppedImg = canvasScaled.toDataURL()
      setPic({
        ...pic,
        img: null,
        cropperOpen: false,
        croppedImg: croppedImg
      })
    }
    setOp(false)
  }

  const handleFileChange = (e, pic, setPic, setOp) => {
    let url = URL.createObjectURL(e.target.files[0])
    const fileSize = (e.target.files[0]?.size / (1024 * 1024)).toFixed(2)
    if (fileSize > 2) {
      notification.error('Please select image below 2Mb.')
      return false
    }
    setPic({
      ...pic,
      img: url,
      cropperOpen: true,
      imageName: e.target.files[0].name
    })
    setOp(true)
  }

  const checkFields = () => {
    let isValid = true
    if (!cat && !subCat) {
      setIsCatError(true)
      setIsSubCatError(true)
      isValid = false
    }
    if (!cat) {
      setIsCatError(true)
      isValid = false
    } else {
      setIsCatError(false)
    }
    if (!subCat) {
      setIsSubCatError(true)
      isValid = false
    } else {
      setIsSubCatError(false)
    }
    return isValid
  }

  const handleSubmitForm = async data => {
    try {
      setUpdateProfileLoading(true)
      const isValid = checkFields()
      if (!isValid) return
      const formData = new FormData()
      formData.append('name', data.name)
      formData.append('address', data.address)
      if (cat) {
        formData.append('countryID', cat)
      }
      if (subCat) {
        formData.append('stateID', subCat)
      }
      formData.append('city', data.city)
      formData.append('zipcode', data.zipcode)
      if (picture.imageName) {
        let response = await fetch(picture.croppedImg)
        let dataImage = await response.blob()
        let metadata = {
          type: 'image/jpeg'
        }
        let file = new File([dataImage], picture.imageName, metadata)
        formData.append('profilePicture', file)
      }
      if (logo.imageName) {
        let response = await fetch(logo.croppedImg)
        let dataLogo = await response.blob()
        let metadata = {
          type: 'image/jpeg'
        }
        let file = new File([dataLogo], logo.imageName, metadata)
        formData.append('logo', file)
      }

      reset()
      updateProfile(formData, setPicture, setLogo)
    } catch (err) {
      console.log('err:', err)
    } finally {
      setUpdateProfileLoading(false)
    }
  }

  const getCountryList = async () => {
    try {
      const res = await apiGet(apiPath.getCountryList, {})
      setRecordTemp(res?.data?.results?.allCountries)
      setListTemp(res?.data?.results?.allCities)
    } catch (error) {
      console.log('error:', error)
    }
  }

  const getStateList = async (countryId) => {
    const id = asId(countryId)
    if (!id) {
      setStateList([])
      return { allStates: [] }
    }
    try {
      const res = await apiGet(`${apiPath.getStateList}/${id}`, {});
      if (res?.data?.success && Array.isArray(res.data.results.allStates)) {
        setStateList(res.data.results.allStates)
        return res.data.results;
      }
      setStateList([])
      return { allStates: [] };
    } catch (error) {
      console.error('Error loading states:', error);
      setStateList([])
      return { allStates: [] };
    }
  };

  useEffect(() => {
    getCountryList()
  }, [])

  useEffect(() => {
    getStateList(cat)
  }, [cat])

  useEffect(() => {
    if (!pendingStateRef.current || !stateList?.length) return
    const wanted = pendingStateRef.current
    const code = typeof wanted === 'object' ? wanted.code : wanted
    const name = typeof wanted === 'object' ? wanted.name : wanted
    if (applyStateFromPlace(code, name, stateList)) pendingStateRef.current = null
  }, [stateList])

  useEffect(() => {
    updatePageName(t('EDIT_PROFILE'))
  }, [])

  useEffect(() => {
    const address = [user?.address, user?.city].filter(Boolean).join(', ')
    if (!address) return undefined
    let cancelled = false
    geocodeAddress(address)
      .then((extra) => {
        if (cancelled) return
        if (Number.isFinite(Number(extra.latitude)) && Number.isFinite(Number(extra.longitude))) {
          setMapPin((prev) => prev || { latitude: extra.latitude, longitude: extra.longitude })
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [user?.address, user?.city])

  const parseFormattedLocation = (formatted) => {
    const bits = String(formatted || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    if (!bits.length) return {}
    const countryName = bits[bits.length - 1]
    let stateName = ''
    let state = ''
    let postalCode = ''
    let city = ''
    if (bits.length >= 2) {
      const stateZip = bits[bits.length - 2]
      const zipMatch = stateZip.match(/(\d{4,10}(?:-\d+)?)/)
      if (zipMatch) postalCode = zipMatch[1]
      const statePart = stateZip.replace(/(\d{4,10}(?:-\d+)?)/, '').trim()
      if (/^[A-Z]{2}$/.test(statePart)) state = statePart
      else stateName = statePart
    }
    if (bits.length >= 3) city = bits[bits.length - 3]
    return { countryName, stateName, state, postalCode, city }
  }

  const applyStateFromPlace = (stateCode, stateName, list) => {
    const candidates = [stateCode, stateName]
      .filter(Boolean)
      .map((v) => String(v).trim().toLowerCase())
    if (!candidates.length) return false
    const match = (list || []).find((s) => {
      const fields = [s.code, s.abbreviation, s.name]
        .filter(Boolean)
        .map((v) => String(v).trim().toLowerCase())
      return fields.some((field) =>
        candidates.some(
          (wanted) =>
            field === wanted ||
            (wanted.length >= 3 && field.startsWith(wanted)) ||
            (wanted.length >= 5 && (field.includes(wanted) || wanted.includes(field))),
        ),
      )
    })
    if (match?._id) {
      setSubCat(String(match._id))
      setIsSubCatError(false)
      return true
    }
    return false
  }

  const findCountryFromPlace = (countryCode, countryName) => {
    const countries = countriesRef.current || []
    const code = String(countryCode || '').toUpperCase().trim()
    const name = String(countryName || '').trim().toLowerCase()
    const compact = name.replace(/[^a-z]/g, '')
    const aliases = {
      US: ['US', 'USA', 'UNITED STATES', 'UNITED STATES OF AMERICA'],
      IN: ['IN', 'IND', 'INDIA'],
      CA: ['CA', 'CAN', 'CANADA'],
      GB: ['GB', 'UK', 'GBR', 'UNITED KINGDOM', 'GREAT BRITAIN'],
      AE: ['AE', 'UAE', 'UNITED ARAB EMIRATES'],
      AU: ['AU', 'AUS', 'AUSTRALIA'],
    }
    const aliasEntry = Object.entries(aliases).find(([, list]) =>
      list.includes(code) || list.includes(String(countryName || '').toUpperCase()),
    )
    const aliasCode = aliasEntry ? aliasEntry[0] : ''

    return countries.find((c) => {
      const cCode = String(c.countryCode || '').toUpperCase().trim()
      const cName = String(c.name || '').trim().toLowerCase()
      const cCompact = cName.replace(/[^a-z]/g, '')
      if (code && cCode === code) return true
      if (aliasCode && (cCode === aliasCode || aliases[aliasCode].includes(cCode))) return true
      if (name && (cName === name || cCompact === compact)) return true
      if (aliasCode && aliases[aliasCode].includes(cName.toUpperCase())) return true
      return false
    })
  }

  const fillLocationFields = async (parts, formattedAddress, coords) => {
    const fromText = parseFormattedLocation(formattedAddress)
    const city = parts.city || fromText.city
    const stateCode = parts.state || fromText.state
    const stateName = parts.stateName || fromText.stateName
    const countryCode = parts.country || fromText.country
    const countryName = parts.countryName || fromText.countryName
    const postalCode = parts.postalCode || fromText.postalCode
    const line1 =
      formattedAddress ||
      [parts.streetNumber, parts.streetName].filter(Boolean).join(' ') ||
      ''

    if (line1) setValue('address', line1, { shouldDirty: true, shouldValidate: false })
    if (city) setValue('city', city, { shouldDirty: true, shouldValidate: false })
    if (postalCode) setValue('zipcode', postalCode, { shouldDirty: true, shouldValidate: false })

    if (coords && Number.isFinite(Number(coords.latitude)) && Number.isFinite(Number(coords.longitude))) {
      setMapPin({
        latitude: Number(coords.latitude),
        longitude: Number(coords.longitude),
      })
    } else {
      setMapPin(null)
    }

    const countryMatch = findCountryFromPlace(countryCode, countryName)
    pendingStateRef.current = { code: stateCode, name: stateName }

    let list = stateListRef.current || []
    if (countryMatch?._id) {
      const nextId = String(countryMatch._id)
      setIsCatError(false)
      if (String(catRef.current) !== nextId) {
        setSubCat('')
        setCat(nextId)
      }
      const result = await getStateList(nextId)
      list = result?.allStates || []
    }
    applyStateFromPlace(stateCode, stateName, list)
  }

  const handlePlaceSelect = async (place) => {
    if (!place) return
    let parts = { ...(place.addressComponents || {}) }
    let formatted = place.formattedAddress || place.name || ''
    let coords =
      Number.isFinite(Number(place.latitude)) && Number.isFinite(Number(place.longitude))
        ? { latitude: Number(place.latitude), longitude: Number(place.longitude) }
        : null

    if (formatted) {
      setValue('address', formatted, { shouldDirty: true, shouldValidate: false })
    }

    if (formatted) {
      try {
        const extra = await geocodeAddress(formatted)
        const extraParts = extra.addressComponents || {}
        parts = {
          ...extraParts,
          ...Object.fromEntries(Object.entries(parts).filter(([, value]) => value)),
        }
        formatted = extra.formattedAddress || formatted
        if (Number.isFinite(Number(extra.latitude)) && Number.isFinite(Number(extra.longitude))) {
          coords = { latitude: Number(extra.latitude), longitude: Number(extra.longitude) }
        }
      } catch (err) {
        console.error('Geocode selected place failed', err)
      }
    }

    await fillLocationFields(parts, formatted, coords)
  }

  const handleMapLocationSelect = async (addressData) => {
    if (!addressData) return
    const formatted = addressData.formattedAddress || addressData.line1 || ''
    await fillLocationFields(
      {
        city: addressData.city,
        state: addressData.state,
        stateName: addressData.stateName,
        postalCode: addressData.postalCode,
        country: addressData.country,
        countryName: addressData.countryName,
      },
      formatted,
      {
        latitude: addressData.latitude,
        longitude: addressData.longitude,
      },
    )
  }

  return (
    <div>
      <div className='px-3 py-4'>
        <div className='bg-white border border-[#E9EDF9] rounded-lg py-4 '>
          <form onSubmit={handleSubmit(handleSubmitForm)} method='post'>
            <div className='lg:grid-cols-3 grid sm:grid-cols-2'>
              <div className='px-4 py-4 md:px-8'>
                <div className='relative'>
                  <OInputField
                    type='text'
                    inputLabel={
                      <>
                        {t('COMPANY_NAME')}
                        <span className='text-red-500'>*</span>
                      </>
                    }
                    autoFocus
                    wrapperClassName='relative z-0 mb-6 w-full group'
                    name='name'
                    maxLength={50}
                    onInput={e => preventMaxInput(e, 50)}
                    errors={errors}
                    register={register('name', formValidation['companyName'])}
                  />
                </div>
              </div>
              <div className='py-4 px-4 md:px-8'>
                <div className='relative'>
                  <OInputField
                    wrapperClassName='relative z-0  mb-6 w-full group'
                    type='text'
                    name='officialEmail'
                    id='officialEmail'
                    inputLabel={<>{t('O_EMAIL_ID')}</>}
                    maxLength={50}
                    onInput={e => preventMaxInput(e, 50)}
                    disabled
                    register={register(
                      'officialEmail',
                      formValidation['email']
                    )}
                  />
                </div>
              </div>
              <div className='py-4 px-4 md:px-8'>
                <div className='relative'>
                  <OInputField
                    wrapperClassName='relative z-0 mb-6 w-full group'
                    name='companyNumber'
                    inputLabel={<>{t('COMPANY_NUMBER')}</>}
                    type='text'
                    maxLength={15}
                    onInput={e => preventMaxInput(e, 15)}
                    disabled
                    register={register(
                      'companyNumber',
                      formValidation['companyID']
                    )}
                  />
                </div>
              </div>
              <div className='md:px-8 py-4 px-4 '>
                <div className='relative'>
                  <select
                    className='block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0  peer'
                    placeholder=' '
                    value={cat || ''}
                    type='select'
                    name='country'
                    id='country'
                    onChange={e => {
                      setCat(e.target.value)
                      setSubCat('')
                      if (e.target.value) {
                        setIsCatError(false)
                      }
                    }}
                  >
                    <option value={''}>
                      {t('SELECT_COUNTRY')}
                      <span className='text-red-500'>*</span>
                    </option>
                    {Array.isArray(recordsTemp) &&
                      recordsTemp.map((i, key) => (
                        <option key={key} value={i._id}>
                          {i.name}
                        </option>
                      ))}
                  </select>
                  <label
                    htmlFor='country'
                    className='dark:text-gray-400 peer-focus:font-normal absolute text-sm text-[#A5A5A5] duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8'
                  >
                    {t('COUNTRY')}
                    <span className='text-red-500'>*</span>
                  </label>
                  {isCatError && (
                    <ErrorMessage message='Please select country.' />
                  )}
                </div>
              </div>
              <div className='py-4 md:px-8 px-4 '>
                <div className='relative'>
                  <select
                    placeholder=' '
                    value={subCat || ''}
                    onChange={e => {
                      setSubCat(e.target.value)
                      if (e.target.value === '') {
                        setIsSubCatError(true)
                      } else {
                        setIsSubCatError(false)
                      }
                    }}
                    type='select'
                    name='stateID'
                    id='stateID'
                    className='bg-transparent block py-4 px-3 w-full text-sm text-gray-900 border-2 rounded-lg border-[#DFDFDF] dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0  peer'
                  >
                    <option value=''>{t('SELECT_STATE')}</option>
                    {stateList?.length &&
                      stateList?.map(
                        (i, key) =>
                          (
                            <option key={key} value={i?._id}>
                              {i?.name}
                            </option>
                          )
                      )}
                  </select>
                  <label
                    htmlFor='stateID'
                    className='peer-focus:font-normal absolute text-sm text-[#A5A5A5] dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8'
                  >
                    {t('STATE')}
                    <span className='text-red-500'>*</span>
                  </label>
                  {isSubCatError && (
                    <ErrorMessage message='Please select city.' />
                  )}
                </div>
              </div>
              <div className='py-4 px-4 md:px-8'>
                <div className='relative'>
                  <OInputField
                    inputLabel={
                      <>
                        {t('CITY')}
                        <span className='text-red-500'>*</span>
                      </>
                    }
                    type='text'
                    autoFocus
                    maxLength={100}
                    wrapperClassName='relative z-0 mb-6 w-full group'
                    name='city'
                    errors={errors}
                    onInput={e => preventMaxInput(e, 100)}
                    register={register('city', formValidation['city'])}
                  />
                </div>
              </div>
              <div className='py-2 px-4 md:px-8 sm:col-span-2 relative z-20 overflow-visible'>
                <label className='block mb-2 text-sm font-medium text-gray-900'>
                  Search address
                </label>
                <AddressAutocomplete
                  onSelect={handlePlaceSelect}
                  className='border-2'
                  placeholder='Search for a location or address...'
                />
              </div>
              <div className='py-2 px-4 md:px-8'>
                <div className='relative'>
                  <OInputField
                    inputLabel={
                      <>
                        {t('ADDRESS')}
                        <span className='text-red-500'>*</span>
                      </>
                    }
                    type='text'
                    autoFocus
                    maxLength={100}
                    wrapperClassName='relative z-0 w-full group'
                    name='address'
                    errors={errors}
                    onInput={e => preventMaxInput(e, 100)}
                    register={register('address', formValidation['address'])}
                  />
                </div>
              </div>
              <div className='py-2 px-4 md:px-8'>
                <div className='relative'>
                  <OInputField
                    inputLabel={
                      <>
                        {t('ZIPCODE')}
                        <span className='text-red-500'>*</span>
                      </>
                    }
                    type='text'
                    autoFocus
                    maxLength={100}
                    wrapperClassName='relative z-0 w-full group'
                    name='zipcode'
                    errors={errors}
                    onInput={e => preventMaxInput(e, 100)}
                    register={register('zipcode', formValidation['zipcode'])}
                  />
                </div>
              </div>
              <div className='px-4 md:px-8 py-2 sm:col-span-2 lg:col-span-1 lg:row-span-3 lg:row-start-3'>
                <div className='h-full min-h-[240px] rounded-lg overflow-hidden border border-[#E9EDF9]'>
                  <LocationMap
                    latitude={mapPin?.latitude}
                    longitude={mapPin?.longitude}
                    onLocationChange={handleMapLocationSelect}
                    height='240px'
                    zoom={15}
                  />
                </div>
              </div>
              <div className='px-4 md:px-8 py-2 lg:col-span-2'>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <div className='relative'>
                      <label className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
                        {t('COMPANY_PROFILE_IMAGE')} (Only image, upto 2Mb)
                      </label>
                      <input
                        type='file'
                        accept='image/png, image/jpg, image/jpeg'
                        name='image'
                        ref={profilePicRef}
                        onChange={e => {
                          handleFileChange(e, picture, setPicture, setOpen)
                        }}
                        className='hidden'
                      />
                      <div className='w-24 relative'>
                        <OImage
                          src={
                            picture.croppedImg
                              ? picture.croppedImg
                              : user?.profilePicture
                          }
                          fallbackUrl='/images/user.png'
                          className='w-20 h-20'
                          alt=''
                          style={{ borderRadius: '50%' }}
                        />
                        <AiFillCamera
                          className=' bg-gray-100  absolute w-4 rounded-xl cursor-pointer'
                          style={{
                            width: '1.5rem',
                            height: '1.5rem',
                            bottom: '0',
                            right: 0,
                            background: '',
                            padding: '1px'
                          }}
                          onClick={() => profilePicRef?.current?.click()}
                        />
                      </div>
                    </div>
                    <ErrorMessage message={profilePicError} />
                  </div>
                  <div>
                    <div className='relative'>
                      <label className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
                        {t('COMPANY_LOGO')} (Only image, upto 2Mb)
                      </label>
                      <input
                        type='file'
                        accept='image/png, image/jpg, image/jpeg'
                        name='image'
                        ref={profilePicRefLogo}
                        onChange={e => {
                          handleFileChange(e, logo, setLogo, setOpenLogo)
                        }}
                        className='hidden'
                      />
                      <div className='w-24 relative'>
                        <OImage
                          src={logo.croppedImg ? logo.croppedImg : user?.logo}
                          fallbackUrl='/images/user.png'
                          className='w-20 h-20'
                          alt=''
                          style={{ borderRadius: '50%' }}
                        />
                        <AiFillCamera
                          className=' bg-gray-100  absolute w-4 rounded-xl cursor-pointer'
                          style={{
                            width: '1.5rem',
                            height: '1.5rem',
                            bottom: '0',
                            right: 0,
                            background: '',
                            padding: '1px'
                          }}
                          onClick={() => profilePicRefLogo?.current?.click()}
                        />
                      </div>
                    </div>
                    <ErrorMessage message={profilePicError} />
                  </div>
                </div>
              </div>
            </div>

            <div className='flex justify-center mt-4'>
              <OButton
                disabled={
                  !isDirty &&
                  (picture.croppedImg == null || logo.croppedImg == null)
                }
                label='Submit'
                type='submit'
                loading={updateProfileLoading}
              />
            </div>
          </form>
        </div>
      </div>
      <>
        <Dialog
          open={open || openLogo}
          onClose={() => {
            if (open) setOpen(false)
            if (openLogo) setOpenLogo(false)
          }}
          aria-labelledby='alert-dialog-title'
          aria-describedby='alert-dialog-description'
        >
          <DialogContent>
            <DialogContentText id='alert-dialog-description'>
              {(picture.cropperOpen || logo?.cropperOpen) && (
                <div className='pb-0 bg-white shadow-lg mt-[30px]'>
                  <AvatarEditor
                    ref={open ? setEditorRef : setEditorRefLogo}
                    image={open ? picture.img : logo.img}
                    width={200}
                    height={200}
                    border={50}
                    color={[255, 255, 255, 0.6]}
                    rotate={0}
                    scale={open ? picture.zoom : logo.zoom}
                    sc
                    className='m-auto mb-4'
                  />
                  <Slider
                    aria-label='raceSlider'
                    value={open ? picture.zoom : logo.zoom}
                    min={1}
                    max={10}
                    step={0.1}
                    onChange={value => {
                      console.log('value', value)
                      if (open)
                        handleSlider(value.target.value, picture, setPicture)
                      if (openLogo)
                        handleSlider(value.target.value, logo, setLogo)
                    }}
                  ></Slider>
                  <div className='flex items-center justify-center p-6 border-t border-solid border-slate-200 rounded-b'>
                    <button
                      className='text-black bg-[#E1E1E1] font-normal   px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6  ease-linear transition-all duration-150'
                      type='button'
                      onClick={() => {
                        if (open) handleCancel(picture, setPicture, setOpen)
                        if (openLogo) handleCancel(logo, setLogo, setOpenLogo)
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className={` bg-LightBlue cursor-pointer  text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1  ease-linear transition-all duration-150`}
                      type='submit'
                      onClick={() => {
                        if (open)
                          handleSave(
                            picture,
                            setPicture,
                            setOpen,
                            editor,
                            setEditorRef
                          )
                        if (openLogo)
                          handleSave(
                            logo,
                            setLogo,
                            setOpenLogo,
                            editorLogo,
                            setEditorRefLogo
                          )
                      }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}
            </DialogContentText>
          </DialogContent>
        </Dialog>
      </>
    </div>
  )
}

export default PersonalDetails
